<script setup>
import Right from 'assets/icons/right.svg'

// Define some refs
const open = ref(false)
const height = ref('0')
const wrapper = ref(null)

// Set open class
const toggleItemClass = computed(() => open.value ? 'toggle-item-open' : '')

/**
 * Sets the height value based on the open value.
 *
 */
const setHeight = () => {
    height.value = open.value ? `${wrapper.value.scrollHeight}px` : '0'
}

/**
 * Toggles the item by inverting the open value and setting the height.
 *
 */
const toggleItem = () => {
    open.value = !open.value
    setHeight()
}

// Change height item on resize
onMounted(() => {
    if (import.meta.client) {
        let timeout

        const handleResize = () => {
            height.value = 0
            clearTimeout(timeout)
            timeout = setTimeout(() => {
                setHeight()
            }, 250)
        }

        window.addEventListener('resize', handleResize)

        onBeforeUnmount(() => {
            window.removeEventListener('resize', handleResize)
        })
    }
})
</script>

<template>
    <div class="toggle-item" :class="toggleItemClass">
        <h3 class="toggle-item-header" @click="toggleItem">
            <slot name="title" />
            <Right />
        </h3>

        <div ref="wrapper" class="toggle-item-content-wrapper">
            <div class="toggle-item-content">
                <slot name="content" />
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.toggle-item {
    max-width: $component-width;
    margin-bottom: 10px;
    background: $toggle-item-bg;
    transition: background-color 600ms ease-in-out;
    border-radius: 20px;

    @include bp(medium1) {
        margin: 0 auto 10px;
    }

    @include bp(large1) {
        margin: 0 auto 15px;
    }

    &.toggle-item-open {
        background: $toggle-item-open-bg;
    }
}

.toggle-item-header {
    cursor: pointer;
    margin: 0;
    padding: 20px 50px 20px 20px;
    position: relative;

    @include body1-medium;

    @include bp(medium1) {
        padding: 24px 50px 24px 24px;
    }

    .toggle-item-open & {
        font-weight: 800;
    }

    svg {
        height: 14px;
        margin-left: 10px;
        transition: transform 600ms ease-in-out;
        transform: rotate(-270deg);
        position: absolute;
        top: 24px;
        right: 20px;

        @include bp(medium1) {
            top: 28px;
            right: 20px;
        }

        .toggle-item-open & {
            transform: rotate(-90deg);
        }
    }
}

.toggle-item-content-wrapper {
    transition: height 600ms ease-in-out;
    overflow: hidden;
    height: v-bind('height');
}

.toggle-item-content {
    max-width: 800px;
    padding: 0 24px 24px;

    p {
        margin: 0;

        @include body1-medium;

        a {
            color: inherit;
            text-decoration: underline;
        }
    }
}
</style>
